import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AlertCircle,
  Clock,
  Layers,
  MessageSquare,
  Percent,
  Receipt,
  RotateCcw,
  Trophy,
  UserPlus,
  Users,
  UserX,
  Wallet,
  XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import DashboardAlertBanner from '@/components/dashboard/DashboardAlertBanner'
import DashboardFilters from '@/components/dashboard/DashboardFilters'
import DashboardSection from '@/components/dashboard/DashboardSection'
import MetricCard from '@/components/dashboard/MetricCard'
import DailySalesChart from '@/components/dashboard/DailySalesChart'
import DistributionDonut from '@/components/dashboard/DistributionDonut'
import FunnelAccordion from '@/components/dashboard/FunnelAccordion'
import RankingBars, { type RankingItem } from '@/components/dashboard/RankingBars'
import { useConnectionAlerts } from '@/hooks/useConnectionAlerts'
import { usePipelines } from '@/hooks/usePipelines'
import { useDashboardCrmMetrics, useDashboardCrmSnapshot } from '@/hooks/useDashboardCrm'
import { defaultDashboardPeriod, type DashboardPeriod } from '@/lib/dashboardPeriod'
import {
  formatNumber,
  formatPercent,
  isCurrencyAbbreviated,
} from '@/lib/dashboardFormat'
import { useCurrency } from '@/hooks/useCurrency'

const Dashboard: React.FC = () => {
  const { t } = useTranslation('reports')
  const { formatCurrency, formatCurrencyCompact } = useCurrency()
  const [period, setPeriod] = useState<DashboardPeriod>(() => defaultDashboardPeriod())
  const [pipelineId, setPipelineId] = useState<string | null>(null)

  const alerts = useConnectionAlerts()
  const { pipelines, loading: pipelinesLoading } = usePipelines()
  const metrics = useDashboardCrmMetrics(period, pipelineId)
  const snapshot = useDashboardCrmSnapshot(pipelineId)

  const summary = metrics.data.summary
  const comparisonLabel = metrics.comparison.label

  const salesRanking = useMemo<RankingItem[]>(() => {
    const rows = metrics.data.won_by_user
    const totalValue = rows.reduce((sum, row) => sum + row.value, 0)

    return rows
      .slice()
      .sort((a, b) => b.value - a.value || b.count - a.count)
      .map((row) => ({
        id: row.user_id ?? 'unassigned',
        label: row.user_id ? row.name : t('dashboard.unassignedLabel'),
        primary: formatCurrencyCompact(row.value),
        secondary: t('dashboard.salesCount', { count: row.count }),
        // Share of revenue, which is what the currency figure beside it represents
        pct: totalValue > 0 ? (row.value / totalValue) * 100 : 0,
      }))
  }, [metrics.data.won_by_user, formatCurrencyCompact, t])

  const lossRanking = useMemo<RankingItem[]>(
    () =>
      metrics.data.lost_by_user.map((row) => ({
        id: row.user_id ?? 'unassigned',
        label: row.user_id ? row.name : t('dashboard.unassignedLabel'),
        primary: formatNumber(row.count),
        pct: row.pct,
      })),
    [metrics.data.lost_by_user, t],
  )

  const lossReasonSlices = useMemo(
    () =>
      metrics.data.loss_reasons.map((row) => ({
        id: row.reason_id ?? 'sem-motivo',
        label: row.name,
        value: row.count,
        pct: row.pct,
      })),
    [metrics.data.loss_reasons],
  )

  const flowSlices = useMemo(() => {
    const flow = metrics.data.period_flow
    const total = flow.opened + flow.won + flow.lost

    return [
      { id: 'open', label: t('dashboard.flowOpen'), value: flow.opened },
      { id: 'won', label: t('dashboard.flowWon'), value: flow.won },
      { id: 'lost', label: t('dashboard.flowLost'), value: flow.lost },
    ]
      .filter((slice) => slice.value > 0)
      .map((slice) => ({ ...slice, pct: total > 0 ? (slice.value / total) * 100 : 0 }))
  }, [metrics.data.period_flow, t])

  const chatRanking = useMemo<RankingItem[]>(() => {
    const { by_user: byUser, open_chats_total: total } = snapshot.data.operation

    return byUser.map((row) => ({
      id: row.user_id,
      label: row.user_id ? row.name : t('dashboard.unassignedLabel'),
      primary: formatNumber(row.count),
      pct: total > 0 ? (row.count / total) * 100 : 0,
    }))
  }, [snapshot.data.operation, t])

  const openTotal = snapshot.data.open_total
  const pipelineTotal = snapshot.data.pipeline_total
  const isAllPipelines = pipelineId === null
  const funnelScopeLabel = isAllPipelines ? t('dashboard.scopeAll') : t('dashboard.scopeThis')
  // The breakdown is the point of this card: it is the number the user could not read
  // anywhere without adding up three figures spread across the page
  const pipelineTotalHelper =
    pipelineTotal.count > 0
      ? t('dashboard.pipelineTotalHelper', {
          open: formatNumber(pipelineTotal.open),
          won: formatNumber(pipelineTotal.won),
          lost: formatNumber(pipelineTotal.lost),
        })
      : t('dashboard.pipelineTotalEmpty', { scope: funnelScopeLabel })

  const priceCoverageHelper =
    openTotal.count > 0 && openTotal.count_with_price < openTotal.count
      ? t('dashboard.priceCoverageHelper', {
          withPrice: formatNumber(openTotal.count_with_price),
          total: formatNumber(openTotal.count),
        })
      : undefined

  const salesRankingNote = metrics.data.won_by_user.some((row) => !row.user_id)
    ? t('dashboard.salesRankingNoteUnassigned', { label: t('dashboard.unassignedLabel') })
    : t('dashboard.salesRankingNote')

  const snapshotPipelineName = snapshot.data.pipeline?.name
  const funnelDescription = t('dashboard.sectionFunnelDesc')

  const error = metrics.error || snapshot.error

  return (
    <div className="flex w-full flex-col gap-8 pb-10">
      {!alerts.loading && (
        <DashboardAlertBanner
          whatsappDisconnected={alerts.whatsappDisconnected}
          openaiMissing={alerts.openaiMissing}
        />
      )}

      <DashboardFilters
        period={period}
        onPeriodChange={setPeriod}
        pipelines={pipelines.map((pipeline) => ({ id: pipeline.id, name: pipeline.name }))}
        pipelineId={pipelineId}
        onPipelineChange={setPipelineId}
        pipelinesLoading={pipelinesLoading}
        comparisonLabel={comparisonLabel}
      />

      {error && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-[20px] border border-red-200 bg-red-50 p-4 dark:border-red-500/30 dark:bg-red-500/10">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-[#DC2626]" />
            <div className="flex flex-col">
              <p className="text-[14px] font-medium text-foreground">
                {t('dashboard.errorTitle')}
              </p>
              <p className="text-[12px] text-muted-foreground">{error}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              void metrics.refetch()
              void snapshot.refetch()
            }}
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            {t('dashboard.retry')}
          </Button>
        </div>
      )}

      <DashboardSection
        title={t('dashboard.sectionSummary')}
        description={t('dashboard.sectionSummaryDesc')}
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            label={t('dashboard.kpiWonValue')}
            value={formatCurrencyCompact(summary.won_value)}
            icon={Wallet}
            accent="green"
            current={summary.won_value}
            previous={summary.prev.won_value}
            comparisonLabel={comparisonLabel}
            helper={
              isCurrencyAbbreviated(summary.won_value)
                ? formatCurrency(summary.won_value)
                : undefined
            }
            loading={metrics.loading}
          />
          <MetricCard
            label={t('dashboard.kpiWonCount')}
            value={formatNumber(summary.won_count)}
            icon={Trophy}
            accent="green"
            current={summary.won_count}
            previous={summary.prev.won_count}
            comparisonLabel={comparisonLabel}
            loading={metrics.loading}
          />
          <MetricCard
            label={t('dashboard.kpiLostCount')}
            value={formatNumber(summary.lost_count)}
            icon={XCircle}
            accent="red"
            current={summary.lost_count}
            previous={summary.prev.lost_count}
            comparisonLabel={comparisonLabel}
            lowerIsBetter
            loading={metrics.loading}
          />
          <MetricCard
            label={t('dashboard.kpiAvgTicket')}
            value={formatCurrencyCompact(summary.avg_ticket)}
            icon={Receipt}
            accent="blue"
            current={summary.avg_ticket}
            previous={summary.prev.avg_ticket}
            comparisonLabel={comparisonLabel}
            helper={t('dashboard.kpiAvgTicketHelper')}
            loading={metrics.loading}
          />
        </div>
      </DashboardSection>

      <DashboardSection title={t('dashboard.sectionEntry')}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <MetricCard
            label={t('dashboard.kpiLeadEntryConv')}
            description={t('dashboard.kpiLeadEntryConvDesc')}
            value={formatPercent(summary.lead_entry_conversion_rate)}
            detail={t('dashboard.kpiLeadEntryDetail', {
              won: t('dashboard.wonDeals', { count: summary.lead_entry_won_count }),
              leads: t('dashboard.leadsReceived', { count: summary.new_leads }),
            })}
            icon={Percent}
            accent="purple"
            current={summary.lead_entry_conversion_rate}
            previous={summary.prev.lead_entry_conversion_rate}
            comparisonLabel={comparisonLabel}
            helper={t('dashboard.kpiLeadEntryConvHelper')}
            loading={metrics.loading}
          />
          <MetricCard
            label={t('dashboard.kpiCloseRate')}
            description={t('dashboard.kpiCloseRateDesc')}
            value={formatPercent(summary.conversion_rate)}
            detail={t('dashboard.kpiCloseRateDetail', {
              won: t('dashboard.wonDeals', { count: summary.won_count }),
              closed: t('dashboard.dealsClosed', { count: summary.won_count + summary.lost_count }),
            })}
            icon={Trophy}
            accent="blue"
            current={summary.conversion_rate}
            previous={summary.prev.conversion_rate}
            comparisonLabel={comparisonLabel}
            helper={t('dashboard.kpiCloseRateHelper')}
            loading={metrics.loading}
          />
          <MetricCard
            label={t('dashboard.kpiNewLeads')}
            description={t('dashboard.kpiNewLeadsDesc')}
            value={formatNumber(summary.new_leads)}
            icon={UserPlus}
            accent="orange"
            current={summary.new_leads}
            previous={summary.prev.new_leads}
            comparisonLabel={comparisonLabel}
            helper={t('dashboard.kpiNewLeadsHelper')}
            loading={metrics.loading}
          />
        </div>
      </DashboardSection>

      <DashboardSection
        title={t('dashboard.sectionWon')}
        description={t('dashboard.sectionWonDesc')}
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <DailySalesChart data={metrics.data.daily} loading={metrics.loading} />
          </div>
          <RankingBars
            title={t('dashboard.salesByAgent')}
            description={salesRankingNote}
            items={salesRanking}
            tone="positive"
            emptyMessage={t('dashboard.noSalesInPeriod')}
            loading={metrics.loading}
          />
        </div>
      </DashboardSection>

      <DashboardSection
        title={t('dashboard.sectionLost')}
        description={t('dashboard.sectionLostDesc')}
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <DistributionDonut
            title={t('dashboard.lossReasons')}
            description={t('dashboard.lossReasonsDesc')}
            slices={lossReasonSlices}
            centerLabel={t('dashboard.lossCenterLabel')}
            emptyMessage={t('dashboard.noLossInPeriod')}
            loading={metrics.loading}
          />
          <RankingBars
            title={t('dashboard.lossByAgent')}
            description={t('dashboard.lossByAgentDesc')}
            items={lossRanking}
            tone="negative"
            emptyMessage={t('dashboard.noLossInPeriod')}
            loading={metrics.loading}
          />
        </div>
      </DashboardSection>

      <DashboardSection
        title={t('dashboard.sectionFunnel')}
        badge={isAllPipelines ? t('dashboard.filterAllFunnels') : snapshotPipelineName}
        description={funnelDescription}
      >
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="flex flex-col gap-4 lg:col-span-2">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <MetricCard
                label={t('dashboard.kpiOpenValue')}
                value={formatCurrencyCompact(openTotal.value)}
                icon={Wallet}
                accent="blue"
                helper={
                  priceCoverageHelper ??
                  (isCurrencyAbbreviated(openTotal.value)
                    ? formatCurrency(openTotal.value)
                    : undefined)
                }
                loading={snapshot.loading}
              />
              <MetricCard
                label={t('dashboard.kpiOpenLeads')}
                value={formatNumber(openTotal.count)}
                icon={Layers}
                accent="slate"
                helper={t('dashboard.kpiOpenLeadsHelper', { scope: funnelScopeLabel })}
                loading={snapshot.loading}
              />
              <MetricCard
                label={isAllPipelines ? t('dashboard.kpiTotalLeads') : t('dashboard.kpiTotalLeadsFunnel')}
                value={formatNumber(pipelineTotal.count)}
                icon={Users}
                accent="purple"
                helper={pipelineTotalHelper}
                loading={snapshot.loading}
              />
            </div>
            <FunnelAccordion pipelines={snapshot.data.pipelines} loading={snapshot.loading} />
          </div>

          <DistributionDonut
            title={t('dashboard.flowTitle')}
            description={t('dashboard.flowDesc')}
            slices={flowSlices}
            centerLabel={t('dashboard.flowCenterLabel')}
            emptyMessage={t('dashboard.noFlowInPeriod')}
            loading={metrics.loading}
            palette={['var(--chart-1)', 'var(--chart-2)', 'var(--chart-4)']}
          />
        </div>
      </DashboardSection>

      <DashboardSection
        title={t('dashboard.sectionChats')}
        badge={t('dashboard.sectionChatsBadge')}
        description={t('dashboard.sectionChatsDesc')}
      >
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <MetricCard
              label={t('dashboard.kpiOpenChats')}
              value={formatNumber(snapshot.data.operation.open_chats_total)}
              icon={MessageSquare}
              accent="blue"
              loading={snapshot.loading}
            />
            <MetricCard
              label={t('dashboard.kpiUnassigned')}
              value={formatNumber(snapshot.data.operation.unassigned)}
              icon={UserX}
              accent="orange"
              helper={t('dashboard.kpiUnassignedHelper')}
              loading={snapshot.loading}
            />
            <MetricCard
              label={t('dashboard.kpiStale', { hours: snapshot.data.operation.stale_hours })}
              value={formatNumber(snapshot.data.operation.awaiting_reply)}
              icon={Clock}
              accent="red"
              helper={t('dashboard.kpiStaleHelper')}
              loading={snapshot.loading}
            />
          </div>
          <RankingBars
            title={t('dashboard.chatsByUser')}
            description={t('dashboard.chatsByUserDesc')}
            items={chatRanking}
            tone="neutral"
            emptyMessage={t('dashboard.noOpenChats')}
            loading={snapshot.loading}
            maxItems={8}
          />
        </div>
      </DashboardSection>
    </div>
  )
}

export default Dashboard
